'use client'

import { WarningCircle } from '@phosphor-icons/react'
import * as Label from '@radix-ui/react-label'
import { VariantProps, cva } from 'class-variance-authority'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef, useId } from 'react'
import { cn } from '@/core/lib/utils'

export const textareaStyle = cva(
  'rounded-sm border pl-3 transition-colors duration-200 hover:duration-150 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        small: 'text-sm',
        normal: 'text-base',
      },
      color: {
        blue: [
          'border-blue-125 bg-blue-25',
          'focus:border-purple-600 focus:ring-1 focus:ring-purple-600',
          'outline-none',
          'disabled:border-transparent disabled:bg-blue-50 disabled:text-gray-600',
        ],
        white: [
          'border-blue-125 bg-white',
          'focus:border-purple-600 focus:ring-1 focus:ring-purple-600',
          'outline-none',
          'disabled:border-transparent disabled:text-gray-600',
        ],
        'white-blue': [
          'border-gray-300 bg-white',
          'focus:border-blue-700 focus:ring-1 focus:ring-blue-700',
          'outline-none',
          'disabled:border-transparent disabled:text-gray-600',
        ],
      },
    },
    defaultVariants: {
      size: 'normal',
      color: 'blue',
    },
  }
)

const textareaLabelStyle = cva(undefined, {
  variants: {
    type: {
      float: [
        'absolute left-3 origin-left cursor-text text-sm text-gray-600 transition-all peer-disabled:text-gray-500',
        'pointer-events-none',
        'top-[6px] scale-75',
        'peer-placeholder-shown:top-[22.5px] peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100',
        'peer-focus:top-[6px] peer-focus:translate-y-0 peer-focus:scale-75',
      ],
      block: '-order-1 mb-1 block text-sm',
    },
  },
  defaultVariants: {
    type: 'float',
  },
})

export type TextAreaProps = Omit<
  ComponentPropsWithoutRef<'textarea'>,
  'children'
> &
  VariantProps<typeof textareaStyle> & {
    label: string
    labelType?: 'float' | 'block' | 'placeholder'
    error?: string
    hideErrorMessage?: boolean
  }

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      size = 'normal',
      color = 'blue',
      label,
      labelType = 'float',
      error,
      hideErrorMessage,
      ...props
    },
    ref
  ) => {
    const textareaId = useId()

    return (
      <div>
        <div className="relative flex w-full flex-col">
          <textarea
            ref={ref}
            id={textareaId}
            placeholder={labelType === 'placeholder' ? label : ' '}
            className={cn(
              textareaStyle({ size, color }),
              {
                'pt-[22.5px] pb-[7.5px]': labelType === 'float',
                'pt-2.5': labelType === 'block' || labelType === 'placeholder',
                'border-red-600 pr-12 focus:border-red-600 focus:ring-1 focus:ring-red-600':
                  !!error,
              },
              'peer',
              className
            )}
            {...props}
          ></textarea>
          {labelType !== 'placeholder' && (
            <Label.Root
              htmlFor={textareaId}
              className={cn(textareaLabelStyle({ type: labelType }))}
            >
              {label}
            </Label.Root>
          )}
          {error && (
            <WarningCircle
              weight="fill"
              size={20}
              className={clsx('absolute right-3 text-red-600', {
                'top-3': labelType !== 'block',
                'top-8': labelType === 'block',
              })}
            />
          )}
        </div>
        {error && !hideErrorMessage && (
          <p className="mt-1 ml-0.5 text-xs text-red-600">{error}</p>
        )}
      </div>
    )
  }
)
// @ts-ignore
TextArea.displayName = 'core-textarea'

export { TextArea }
